import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { AlertCircle, CreditCard, Mail } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '@/lib/supabaseClient';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export default function SubscriptionRequired() {
  const { session } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(false);

  useEffect(() => {
    // Vérifier si l'email est confirmé
    const checkEmailConfirmation = async () => {
      if (!session?.user) return;
      
      // Dans Supabase, si email_confirmed_at existe, l'email est confirmé
      const confirmed = session.user.email_confirmed_at !== null;
      setEmailConfirmed(confirmed);
    };

    checkEmailConfirmation();
  }, [session]);

  const handleGoToPayment = async () => {
    setProcessing(true);
    
    try {
      const pendingPlan = localStorage.getItem('pendingPlan') || 'standard';
      const priceId = pendingPlan === 'standard' 
        ? process.env.REACT_APP_STRIPE_PRICE_STANDARD 
        : process.env.REACT_APP_STRIPE_PRICE_PREMIUM;

      // Via l'edge function Supabase (et NON /api/, proxifié vers le backend Python).
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: { priceId, plan: pendingPlan },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message || "checkout_error");

      if (data?.url) {
        window.location.href = data.url;
      } else if (data?.sessionId) {
        const stripe = await stripePromise;
        await stripe.redirectToCheckout({ sessionId: data.sessionId });
      }
    } catch (err) {
      alert('Erreur : ' + err.message);
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 overflow-hidden">
          
          {/* Header */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-12 text-center border-b border-amber-200">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full shadow-md mb-6">
              <AlertCircle className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-3xl font-serif text-gray-900 mb-3">
              Abonnement requis
            </h1>
            <p className="text-neutral-600 text-lg">
              Pour accéder à votre espace ARTKIV, vous devez finaliser votre abonnement
            </p>
          </div>

          <div className="p-8 space-y-6">
            
            {/* Statut email */}
            {emailConfirmed ? (
              <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl border border-green-200">
                <Mail className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-green-900">
                  <strong className="block mb-1">✓ Email confirmé</strong>
                  Votre adresse email a été vérifiée avec succès.
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-900">
                  <strong className="block mb-1">Email non confirmé</strong>
                  Veuillez vérifier votre boîte email et cliquer sur le lien de confirmation avant de procéder au paiement.
                </div>
              </div>
            )}

            {/* Information sur l'accès */}
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Pourquoi finaliser mon paiement ?
              </h3>
              <ul className="space-y-2 text-sm text-blue-900">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>Accès complet à votre espace de gestion de collection</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>Ajout illimité d'œuvres d'art</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>Outils d'estimation et de suivi de valeur</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>Support client prioritaire</span>
                </li>
              </ul>
            </div>

            {/* Plan sélectionné */}
            <div className="border-t border-neutral-200 pt-6">
              <p className="text-sm text-neutral-600 mb-4">
                Plan sélectionné : <strong className="text-neutral-900">
                  {localStorage.getItem('pendingPlan') === 'premium' ? 'Premium (199€/mois)' : 'Standard (29€/mois)'}
                </strong>
              </p>
              
              {new Date() < new Date("2026-02-15T23:59:59Z") && 
               localStorage.getItem('pendingPlan') === 'standard' && (
                <p className="text-sm text-green-700 font-medium mb-4">
                  🎁 Profitez de 14 jours d'essai gratuit !
                </p>
              )}

              <button
                onClick={handleGoToPayment}
                disabled={processing || !emailConfirmed}
                className={`
                  w-full py-4 px-6 rounded-lg font-medium transition-all text-base
                  ${processing || !emailConfirmed
                    ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                    : 'bg-neutral-900 text-white hover:bg-neutral-800'
                  }
                `}
              >
                {processing ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⏳</span>
                    Redirection...
                  </span>
                ) : !emailConfirmed ? (
                  'Confirmez votre email d\'abord'
                ) : (
                  'Finaliser mon paiement'
                )}
              </button>
            </div>

            {/* Aide */}
            <div className="border-t border-neutral-200 pt-6 text-center">
              <p className="text-sm text-neutral-600 mb-2">
                Besoin d'aide ou vous avez changé d'avis ?
              </p>
              <div className="flex gap-3 justify-center">
                <a 
                  href="/contact" 
                  className="text-sm text-neutral-900 underline hover:text-black font-medium"
                >
                  Contactez-nous
                </a>
                <span className="text-neutral-300">•</span>
                <a 
                  href="/pricing" 
                  className="text-sm text-neutral-900 underline hover:text-black font-medium"
                >
                  Changer de plan
                </a>
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}